import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPageLavender from "./pages/LandingPageLavender";
import GatedLandingPage from "./pages/GatedLandingPage";
import BrowsePage from "./pages/BrowsePage";
import LibraryPage from "./pages/LibraryPage";
import FavoritesPage from "./pages/FavoritesPage";
import PlaylistPage from "./pages/PlaylistPage";
import DiscoverPlaylistsPage from "./pages/DiscoverPlaylistsPage";
import SharedPlaylistPage from "./pages/SharedPlaylistPage";
import SubscribePage from "./pages/SubscribePage";
import PacksPage from "./pages/PacksPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import CreditsSuccessPage from "./pages/CreditsSuccessPage";
import BuyCreditsPage from "./pages/BuyCreditsPage";
import AccountPage from "./pages/AccountPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import Layout from "./components/Layout";

// Community Pages
import CommunityPage from "./pages/CommunityPage";
import CategoryPostsPage from "./pages/CategoryPostsPage";
import PostDetailPage from "./pages/PostDetailPage";
import MessagesPage from "./pages/MessagesPage";

// Legal Pages
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";

// Components
import CookieConsent from "./components/CookieConsent";
import { PlayerProvider } from "./contexts/PlayerContext";

// Admin Pages
import AdminLayout, { AdminAuthProvider, useAdminAuth } from "./pages/admin/AdminLayout";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminSiteConfig from "./pages/admin/AdminSiteConfig";
import AdminVibes from "./pages/admin/AdminVibes";
import AdminBundles from "./pages/admin/AdminBundles";
import AdminPromoCodes from "./pages/admin/AdminPromoCodes";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminBulkUpload from "./pages/admin/AdminBulkUpload";
import AdminFileManager from "./pages/admin/AdminFileManager";
import AdminContentManager from "./pages/admin/AdminContentManager";
import AdminLandingPage from "./pages/admin/AdminLandingPage";
import AdminPackManager from "./pages/admin/AdminPackManager";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import PreviewPage from "./pages/PreviewPage";
import SongPreviewPage from "./pages/SongPreviewPage";
import AdminPreviewPage from "./pages/admin/AdminPreviewPage";
import AdminQuizzes from "./pages/admin/AdminQuizzes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Check ONCE at page load if we're on the preview page with a token
const previewUrlParams = new URLSearchParams(window.location.search);
const isPreviewPage = window.location.pathname === '/preview' && previewUrlParams.has('token');

// If on preview page, store the token immediately before React even renders
if (isPreviewPage) {
  const previewToken = previewUrlParams.get('token');
  if (previewToken) {
    localStorage.setItem('token', previewToken);
    // Don't redirect - just set a flag that we're in preview mode
    sessionStorage.setItem('preview_mode', 'true');
  }
}

const PreviewRoute = ({ legacy }) => (isPreviewPage ? legacy : <SongPreviewPage />);

// Check if we have an auth token (either from preview or regular login)
const hasStoredToken = localStorage.getItem('token');

export const API = `${BACKEND_URL}/api`;

// Configure axios defaults - credentials disabled for Safari CORS compatibility
axios.defaults.withCredentials = false;

// CRITICAL: Interceptor to FORCE withCredentials=false on ALL requests (Safari fix)
axios.interceptors.request.use(
  (config) => {
    // Force withCredentials to false for Safari CORS compatibility
    config.withCredentials = false;
    
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Theme Context — exposes current theme name and a setter so admin UI can flip it live
const ThemeContext = createContext({ theme: "lavender", setTheme: () => {}, refreshTheme: () => {} });
export const useTheme = () => useContext(ThemeContext);

/**
 * ThemeProvider — kept as a no-op stub so existing `useTheme()` callers keep working,
 * but the gold/dark theme has been retired. Lavender is now baked into :root in
 * index.css — no class toggling needed. This also fixes the brief mobile-Safari
 * gold "flash" on first paint that came from late class application.
 */
export const ThemeProvider = ({ children }) => {
  // Clean up any leftover state from the old multi-theme era (auto-migration)
  useEffect(() => {
    document.documentElement.classList.remove("theme-lavender");
    try { localStorage.removeItem("site_theme"); } catch { /* ignore quota / private mode */ }
  }, []);

  const value = {
    theme: "lavender",
    setTheme: () => {},     // no-op: theme is no longer admin-configurable
    refreshTheme: () => {}, // no-op
  };
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      localStorage.removeItem("token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setUser(response.data.user);
    setToken(response.data.token);
    localStorage.setItem("token", response.data.token);
    return response.data;
  };

  const register = async (name, email, password, dateOfBirth, address, phone) => {
    const response = await axios.post(`${API}/auth/register`, { 
      name, 
      email, 
      password,
      date_of_birth: dateOfBirth,
      address,
      phone
    });
    setUser(response.data.user);
    setToken(response.data.token);
    localStorage.setItem("token", response.data.token);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (e) { /* ignore */ }
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
  };

  const setAuthUser = (userData, authToken) => {
    setUser(userData);
    if (authToken) {
      setToken(authToken);
      localStorage.setItem("token", authToken);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, token, login, register, logout, checkAuth, setAuthUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-gold animate-pulse font-serif text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Protected Route
const AdminProtectedRoute = ({ children }) => {
  const { admin, loading } = useAdminAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-gold animate-pulse font-serif text-xl">Loading...</div>
      </div>
    );
  }

  if (!admin) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// App Router
// Legacy ThemedLanding removed Feb 2026 — lavender is now the only theme.
// LandingPageLavender is imported directly wherever needed.

function AppRouter() {
  const location = useLocation();
  const { user, loading: authLoading, token: authToken } = useAuth();
  const [gatedMode, setGatedMode] = useState(null); // null = loading, true/false = loaded
  const [gatedModeLoading, setGatedModeLoading] = useState(true);
  
  // Fetch gated mode setting from API on mount
  useEffect(() => {
    const fetchGatedMode = async () => {
      try {
        const response = await axios.get(`${API}/site-config/gated-mode`, { withCredentials: false });
        setGatedMode(response.data.gated_mode);
      } catch (error) {
        console.error('Failed to fetch gated mode, defaulting to true:', error);
        setGatedMode(true); // Default to gated on error
      } finally {
        setGatedModeLoading(false);
      }
    };
    fetchGatedMode();
  }, []);
  
  // Track page views - must be before any early returns to follow React hooks rules
  useEffect(() => {
    if (gatedModeLoading || authLoading) return; // Skip tracking while loading
    const path = location.pathname;
    if (!path.startsWith("/admin")) {
      axios.post(`${API}/analytics/pageview`, {
        page: path,
        referrer: document.referrer,
      }).catch(() => {});
    }
  }, [location.pathname, gatedModeLoading, authLoading]);

  // Check for session_id in hash (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Show loading while fetching gated mode setting
  if (gatedModeLoading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-gold animate-pulse font-serif text-xl">Loading...</div>
      </div>
    );
  }

  // Bypass gated mode if user is logged in (uses live React state, not stale module constant)
  const effectiveGatedMode = gatedMode && !authToken && !hasStoredToken;
  
  // GATED MODE: Show only the landing page for public users (unless they have a token)
  if (effectiveGatedMode) {
    return (
      <AdminAuthProvider>
        <Routes>
          {/* Public gated landing page */}
          <Route path="/" element={<GatedLandingPage />} />
          
          {/* Legal pages still accessible */}
          <Route path="privacy" element={<PrivacyPolicyPage />} />
          <Route path="terms" element={<TermsOfServicePage />} />
          
          {/* Admin routes - all inside AdminAuthProvider */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminProtectedRoute><AdminLayout><AdminDashboard /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/site-config" element={<AdminProtectedRoute><AdminLayout><AdminSiteConfig /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/vibes" element={<AdminProtectedRoute><AdminLayout><AdminVibes /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/bundles" element={<AdminProtectedRoute><AdminLayout><AdminBundles /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/promo-codes" element={<AdminProtectedRoute><AdminLayout><AdminPromoCodes /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/orders" element={<AdminProtectedRoute><AdminLayout><AdminOrders /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/customers" element={<AdminProtectedRoute><AdminLayout><AdminCustomers /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/bulk-upload" element={<AdminProtectedRoute><AdminLayout><AdminBulkUpload /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/file-manager" element={<AdminProtectedRoute><AdminLayout><AdminFileManager /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/content" element={<AdminProtectedRoute><AdminLayout><AdminContentManager /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/landing-page" element={<AdminProtectedRoute><AdminLayout><AdminLandingPage /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/pack-manager" element={<AdminProtectedRoute><AdminLayout><AdminPackManager /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/preview-page" element={<AdminProtectedRoute><AdminLayout><AdminPreviewPage /></AdminLayout></AdminProtectedRoute>} />
          <Route path="/admin/quizzes" element={<AdminProtectedRoute><AdminLayout><AdminQuizzes /></AdminLayout></AdminProtectedRoute>} />
          
          {/* Preview mode - opens full app for admins */}
          <Route path="/preview" element={<PreviewRoute legacy={<PreviewPage />} />} />
          
          {/* Redirect everything else to landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AdminAuthProvider>
    );
  }

  // NORMAL MODE: Full site access
  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<LandingPageLavender />} />
        <Route path="browse" element={<BrowsePage />} />
        <Route path="browse/:category" element={<BrowsePage />} />
        <Route path="library" element={<LibraryPage />} />
        <Route path="library/:category" element={<LibraryPage />} />
        <Route path="favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
        <Route path="playlist" element={<ProtectedRoute><PlaylistPage /></ProtectedRoute>} />
        <Route path="discover" element={<DiscoverPlaylistsPage />} />
        <Route path="playlist/shared/:userId" element={<SharedPlaylistPage />} />
        <Route path="subscribe" element={<SubscribePage />} />
        <Route path="packs" element={<PacksPage />} />
        <Route path="subscription/success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />
        <Route path="credits/success" element={<ProtectedRoute><CreditsSuccessPage /></ProtectedRoute>} />
        <Route path="buy-credits" element={<BuyCreditsPage />} />
        <Route path="account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
        
        {/* Community Forum Routes */}
        <Route path="community" element={<CommunityPage />} />
        <Route path="community/:categoryId" element={<CategoryPostsPage />} />
        <Route path="community/post/:postId" element={<PostDetailPage />} />
        <Route path="messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
        <Route path="messages/:userId" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
        
        {/* Legal Pages */}
        <Route path="privacy" element={<PrivacyPolicyPage />} />
        <Route path="terms" element={<TermsOfServicePage />} />
      </Route>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/preview" element={<PreviewRoute legacy={<Navigate to="/library" replace />} />} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="site-config" element={<AdminSiteConfig />} />
        <Route path="landing-page" element={<AdminLandingPage />} />
        <Route path="vibes" element={<AdminVibes />} />
        <Route path="bundles" element={<AdminBundles />} />
        <Route path="bulk-upload" element={<AdminBulkUpload />} />
        <Route path="file-manager" element={<AdminFileManager />} />
        <Route path="content" element={<AdminContentManager />} />
        <Route path="promo-codes" element={<AdminPromoCodes />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="pack-manager" element={<AdminPackManager />} />
        <Route path="preview-page" element={<AdminPreviewPage />} />
        <Route path="quizzes" element={<AdminQuizzes />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AdminAuthProvider>
            <PlayerProvider>
              <AppRouter />
              <Toaster position="top-right" richColors />
              <CookieConsent />
            </PlayerProvider>
          </AdminAuthProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
